import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { processingApi } from '@/utils/api';
import { useContentStore } from '@/store';

const DIGESTS_KEY = 'digests';
const LESSONS_KEY = 'lessons';
const RUNS_KEY = 'runs';

export function useProcessingRuns(limit?: number) {
  return useQuery({
    queryKey: [RUNS_KEY, limit],
    queryFn: () => processingApi.getRuns(limit),
  });
}

export function useManualRun() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: () => processingApi.manualRun(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [RUNS_KEY] });
      queryClient.invalidateQueries({ queryKey: [DIGESTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [LESSONS_KEY] });
    },
  });
}

export function useDigests(limit?: number) {
  const { setDigests, setLoading } = useContentStore();
  
  return useQuery({
    queryKey: [DIGESTS_KEY, limit],
    queryFn: async () => {
      setLoading(true);
      try {
        const data = await processingApi.getDigests(limit);
        setDigests(data);
        return data;
      } finally {
        setLoading(false);
      }
    },
  });
}

export function useDigest(id: number) {
  const { setSelectedDigest, setLoading } = useContentStore();
  
  return useQuery({
    queryKey: [DIGESTS_KEY, id],
    queryFn: async () => {
      setLoading(true);
      try {
        const data = await processingApi.getDigest(id);
        setSelectedDigest(data);
        return data;
      } finally {
        setLoading(false);
      }
    },
    enabled: !!id,
  });
}

export function useLessons(limit?: number) {
  const { setLessons, setLoading } = useContentStore();
  
  return useQuery({
    queryKey: [LESSONS_KEY, limit],
    queryFn: async () => {
      setLoading(true);
      try {
        const data = await processingApi.getLessons(limit);
        setLessons(data);
        return data;
      } finally {
        setLoading(false);
      }
    },
  });
}

export function useLesson(id: number) {
  const { setSelectedLesson, setLoading } = useContentStore();
  
  return useQuery({
    queryKey: [LESSONS_KEY, id],
    queryFn: async () => {
      setLoading(true);
      try {
        const data = await processingApi.getLesson(id);
        setSelectedLesson(data);
        return data;
      } finally {
        setLoading(false);
      }
    },
    enabled: !!id,
  });
}
